// These legality checkers are all of the companion legality checkers for the currently released 10 companions
// It's possible that we should break out these legality checkers into their own files
//
// If in the future we ever get more companions, we should probably just break out their custom legaliy checkers into
// their own files broken out by set.
//

import { CardType, defaultCard, EDH_FORMATS } from 'types/deck'

import { DeckLeglaity } from './deckLegality'
import { ERRORS } from './legality.const'

const PERMANENTS = ['Artifact', 'Creature', 'Enchantment', 'Land', 'Planeswalker']

type CompanionChecker = (deck: CardType[], legality: DeckLeglaity, companionName: string) => void

// prettier-ignore
const generateCompanionError: (companionName: string) => string = companionName => `${ERRORS.COMPANION_ERROR}"${companionName}" -`

// Your starting deck contains only cards with even mana values
const GyrudaChecker: CompanionChecker = (deck, legality, companionName) => {
  const oddCmcCards = deck.filter(c => c.cmc % 2 !== 0) // Getting all illegal cards

  for (const card of oddCmcCards) legality.addCardError(generateCompanionError(companionName), card)
}

// No card in your starting deck has more than one of the same mana symbol in its mana cost
// Example casting costs data formats
// Sol ring - ["1"]
// Black Market - ["3", "B", "B"]
// Ravenous Squirrel - [["B", "G"]] (Hybrid mana)
const JeganthaChecker: CompanionChecker = (deck, legality, companionName) => {
  const sameManaSymbolCards = deck.filter(card => {
    // Grabs the casting cost of the front of a card if it's s flip card, or the standard casting cost
    const castingCost = [...(card.front?.castingCost || card.castingCost)]

    // For split cards specifically, it counts both mana costs since they're both on the same side of the card
    // This is _not_ true any other layout of card (unless they add one where the casting cost is both on one side)
    if (card.layout === 'split') castingCost.push(...(card.back?.castingCost || []))

    // Converting casting costs to string for hybrid mana, this way the set will see them as the same value
    // Using a set we can uniquify this casting cost down to it's mana symbols
    const castingCostsAsString = castingCost.map(c => c.toString())
    const uniqueCastingCost = new Set(castingCostsAsString)

    return uniqueCastingCost.size !== castingCost.length
  })

  for (const card of sameManaSymbolCards) legality.addCardError(generateCompanionError(companionName), card)
}

// Each creature card in your starting deck is a Cat, Elemental, Nightmare, Dinosaur, or Beast card
const KaheeraChecker: CompanionChecker = (deck, legality, companionName) => {
  const cardsMissingTypes = deck.filter(
    card =>
      card.types.includes('Creature') &&
      !card.subTypes.includes('Cat') &&
      !card.subTypes.includes('Elemental') &&
      !card.subTypes.includes('Nightmare') &&
      !card.subTypes.includes('Dinosaur') &&
      !card.subTypes.includes('Beast') &&
      !card.text.includes('Changeling') &&
      card.name !== 'Mistform Ultimus',
  )

  for (const card of cardsMissingTypes) legality.addCardError(generateCompanionError(companionName), card)
}

// Your starting deck contains only cards with mana value 3 or greater and land cards.
const KerugaChecker: CompanionChecker = (deck, legality, companionName) => {
  const nonLandLessThanThreeCmc = deck.filter(card => !card.types.includes('Land') && card.cmc < 3)

  for (const card of nonLandLessThanThreeCmc) legality.addCardError(generateCompanionError(companionName), card)
}

// Each permanent card in your starting deck has mana value 2 or less
const LurrusChecker: CompanionChecker = (deck, legality, companionName) => {
  const cmcGreaterThanTwo = deck.filter(card => card.cmc > 2 && card.types.some(t => PERMANENTS.includes(t)))

  for (const card of cmcGreaterThanTwo) legality.addCardError(generateCompanionError(companionName), card)
}

// Each nonland card in your starting deck has a different name.
const LutriChecker: CompanionChecker = (deck, legality, companionName) => {
  const fakeLutri = { ...defaultCard, name: 'Lutri, the Spellchaser' }
  const cardsWithMoreThanOneCopy = deck.filter(card => card.qty > 1 && !card.types.includes('Land'))

  if (EDH_FORMATS.includes(legality.format)) legality.addCardError(ERRORS.BANNED, fakeLutri)

  for (const card of cardsWithMoreThanOneCopy) legality.addCardError(generateCompanionError(companionName), card)
}

// Your starting deck contains only cards with odd mana values and land cards.
const OboshChecker: CompanionChecker = (deck, legality, companionName) => {
  const evenCmcNonLandCards = deck.filter(c => c.cmc % 2 === 0 && !c.types.includes('Land')) // Getting all illegal cards

  for (const card of evenCmcNonLandCards) legality.addCardError(generateCompanionError(companionName), card)
}

// Each nonland card in your starting deck shares a card type
const UmoriChecker: CompanionChecker = (deck, legality, companionName) => {
  const creatureInTheDeck = 'Grist, the Hunger Tide'

  // prettier-ignore
  const cardTypeCounts = deck.reduce((acc: Record<string, number>, card) => {
    if (card.types.includes('Land')) return acc

    acc.totalNonLand += card.qty

    // Grist is a creature while not on the field - and therefor what companion actually cares about
    // We're just going to force it to being a creature here
    // Grist is a creature IN ADDITION to it's other types, so we'll just let it loop through it's normal types
    if (card.name === creatureInTheDeck) {
      if (acc['Creature']) acc['Creature'] += card.qty
      else acc['Creature'] = card.qty
    }

    for (const type of card.types) {
      if (acc[type]) acc[type] += card.qty
      else acc[type] = card.qty
    }

    return acc
  }, { totalNonLand: 0 })

  const totalNonLandCards = cardTypeCounts.totalNonLand
  const cardTypes = Object.keys(cardTypeCounts)

  // We use this to choose the assumed type you're going for with Umori
  let topType = 'totalNonLand'
  let topTypeCount = 0

  for (const type of cardTypes) {
    // We can just skip our total counter as it's not a real type
    if (type === 'totalNonLand') continue

    const totalOfCardType = cardTypeCounts[type]

    // Set the
    if (totalOfCardType > topTypeCount) {
      topType = type
      topTypeCount = totalOfCardType
    }

    // If any amount of a given card type matches the total number of non land cards in the deck - return without adding any errors
    // This means that there are the same number of a given type of card as there are non land cards in the deck
    // therefor all nonland cards in the deck share this one type
    if (totalOfCardType === totalNonLandCards) return
  }

  // Grab all cards who aren't lands and don't have the assumed top type
  let missingTopType = deck.filter(card => !card.types.includes('Land') && !card.types.includes(topType))

  // if the top type is creatures then we need to remove the special snowflake of a planeswalker
  if (topType === 'Creature') missingTopType = missingTopType.filter(card => card.name !== creatureInTheDeck)

  // prettier-ignore
  for (const card of missingTopType) legality.addCardError(`Card doesn't match assumed Umori type of ${topType} - `, card)
}

// Your starting deck contains at least twenty cards more than the minimum deck size
const YorionChecker: CompanionChecker = (_deck, legality) => {
  // We don't have access to this card so we're just going to make a generic card with his name for the error object
  const fakeYorion = { ...defaultCard, name: 'Yorion, Sky Nomad' }

  // Might want to look into how this works for formats that have an exact card count (eg: Commander)
  // I think you just can't use this as a companion in those formats
  // EDH formats require an exact amount of cards, so we just assuming
  // prettier-ignore
  if (EDH_FORMATS.includes(legality.format)) legality.addCardError('Your companion cannot be used in formats that require an exact amount of cards - ', fakeYorion)
  else if (legality.deckSize < 80) legality.addCardError('Your companion requires decks to have 20 or more cards than the format minimum - ', fakeYorion)
}

// Each permanent card in your starting deck has an activated ability.
const ZirdaChecker: CompanionChecker = (deck, legality, companionName) => {
  // Pulled this list from scryfall
  const nonStandardActivatedAbilities = [
    // 'Equip', // Doing Equip manually since it has odd implications
    'Cycling',
    'Plainscycling',
    'Islandcycling',
    'Swampcycling',
    'Mountaincycling',
    'Forestcycling',
    'Transfigure',
    'Unearth',
    'Level up',
    'Outlast',
    'Crew',
    'Ninjutsu',
    'Commander ninjutsu',
    'Transmute',
    'Forecast',
    'Aura swap',
    'Reinforce',
    'Scavenge',
    'Embalm',
    'Eternalize',
    'Fortify',
    'Reconfigure',
    'Encore',
    'Craft',
    'Saddle',
  ]

  // I don't feel like figuring out a nice regex. Use this to hard consider some cards illegal
  const cardsThatAreIllegalThatDontMatchANiceRegex = ['Fluctuator']

  const noneActivatedAbilityCards = deck.filter(card => {
    // Tests whether string containers a ":" in it that is not inside of double quotes
    // cost: ability is the format for 99% of activated abilities, but that doesn't include those which
    // are added to other cards (eg: all squirells now have "{T}: add one {G}")
    const regex = new RegExp(/((?!"[\w\s.]*):(?![\w\s.]*"))/)

    const text = card.text.replace(/ *\([^)]*\) */g, '').replace(/"[^"]+"/g, '')
    const frontText = card.front?.text?.replace(/ *\([^)]*\) */g, '').replace(/"[^"]+"/g, '')

    if (cardsThatAreIllegalThatDontMatchANiceRegex.includes(card.name)) return true
    if (card.superTypes.includes('Basic')) return false

    for (const basicLandType of ['Plains', 'Island', 'Swamp', 'Mountain', 'Forest', 'Wastes']) {
      if (card.subTypes.includes(basicLandType)) return false
    }

    // If the card isn't a permanent
    // if the card has a non standard activated ability
    // if the card has a "normal" activated ability eg: {T}: Do the thing
    if (!card.types.some(t => PERMANENTS.includes(t))) return false
    if (nonStandardActivatedAbilities.some(k => text.includes(k))) return false

    // Equip is odd because
    if (
      text
        .replace(/Equipment/g, '')
        .replace(/Equipped/g, '')
        .includes('Equip') ||
      frontText
        ?.replace(/Equipment/g, '')
        ?.replace(/Equipped/g, '')
        .includes('Equip')
    )
      return false

    if (regex.test(text)) return false

    // We have to check the front and backs of cards for activated abilities as well
    if (frontText) {
      if (nonStandardActivatedAbilities.some(k => frontText.includes(k))) return false
      if (regex.test(frontText)) return false
    }

    return true
  })

  for (const card of noneActivatedAbilityCards) legality.addCardError(generateCompanionError(companionName), card)
}

export const CompanionLookup: Record<string, CompanionChecker> = {
  'Gyruda, Doom of Depths': GyrudaChecker,
  'Jegantha, the Wellspring': JeganthaChecker,
  'Kaheera, the Orphanguard': KaheeraChecker,
  'Keruga, the Macrosage': KerugaChecker,
  'Lurrus of the Dream-Den': LurrusChecker,
  'Lutri, the Spellchaser': LutriChecker,
  'Obosh, the Preypiercer': OboshChecker,
  'Umori, the Collector': UmoriChecker,
  'Yorion, Sky Nomad': YorionChecker,
  'Zirda, the Dawnwaker': ZirdaChecker,
}

export default CompanionLookup
